import { dayjs, DEFAULT_PDS_TRACE_FREQUENCY_DAYS, NHS_NUMBER_STATUS_TEXT, nhsNumberStatus, traceIsNeeded, } from "@thalamos/common";
export { DEFAULT_PDS_TRACE_FREQUENCY_DAYS };
/**
 * Whether the patient has an NHS number that came from PDS and that PDS has not
 * since rejected. A rejected link — no match found, or the number retired in
 * favour of another — leaves the patient effectively untraced, so it does not
 * count, and neither does a link holding an EPR's own patient identifier
 * rather than an NHS number.
 *
 * Deliberately coarse: it says nothing about how long ago the trace ran, so a
 * patient last traced years ago still counts. Callers needing to tell a stale
 * trace from a superseded one want `calculateNhsNumberIndicatorStatus`.
 */
export function hasUsablePdsLink(externalLinks) {
    return externalLinks.some((link) => link.externalSystemType === "pds" &&
        link.externalPatientIdType === "nhs.number" &&
        !link.isInvalid);
}
/**
 * The link eMHA would act on for a system: the lowest priority number wins.
 *
 * Only a link keyed on an NHS number qualifies, however high an EPR's own
 * patient identifier ranks. A link's system says where it came from, not what
 * its value is, and a status about the NHS number can only be about a link
 * that holds one.
 */
const highestPriorityNhsNumberLink = (externalLinks, externalSystemType) => {
    const claims = externalLinks.filter((link) => link.externalSystemType === externalSystemType &&
        link.externalPatientIdType === "nhs.number");
    return claims.find((link) => !claims.some((l) => l.priorityWithinExternalSystem < link.priorityWithinExternalSystem));
};
const asClaim = (link) => ({
    rejectedByPds: link.isInvalid,
    // A demographics pull someone processed is what counts here as the number
    // having been confirmed; a pull nobody reviewed leaves this untouched.
    lastTracedAt: link.dateLastProcessed
        ? dayjs(link.dateLastProcessed).toISOString()
        : null,
    source: link,
});
/**
 * eMHA's NHS Number Status Indicator: which of its links stand for the number
 * PDS gave and the number another system did, with the classification itself
 * left to `nhsNumberStatus`.
 *
 * Rio is what "another system" means here. eMHA pulls demographics from it, so
 * a Rio pull that has been processed confirms the number as much as a PDS
 * trace does.
 */
export function calculateNhsNumberIndicatorStatus(externalLinks, suggestedMerges, traceFrequency = {
    value: DEFAULT_PDS_TRACE_FREQUENCY_DAYS,
    unit: "days",
}) {
    const pdsLink = highestPriorityNhsNumberLink(externalLinks, "pds");
    const rioLink = highestPriorityNhsNumberLink(externalLinks, "rio");
    const status = nhsNumberStatus({
        fromPds: pdsLink ? asClaim(pdsLink) : null,
        fromElsewhere: rioLink ? asClaim(rioLink) : null,
        supersessionUnresolved: (suggestedMerges?.length ?? 0) > 0,
    }, traceFrequency);
    // No default: a code this does not answer for leaves the function without a
    // return, which the declared type rejects at compile time.
    switch (status.code) {
        case "01":
            return {
                value: "01",
                text: NHS_NUMBER_STATUS_TEXT["01"],
                link: status.source,
            };
        case "02":
            return {
                value: "02",
                text: NHS_NUMBER_STATUS_TEXT["02"],
                link: status.source,
            };
        case "03":
            return {
                value: "03",
                text: NHS_NUMBER_STATUS_TEXT["03"],
                link: status.source,
            };
        case "04":
            return {
                value: "04",
                text: NHS_NUMBER_STATUS_TEXT["04"],
                link: status.source,
            };
        case "05":
            return {
                value: "05",
                text: NHS_NUMBER_STATUS_TEXT["05"],
                suggestedMerges,
            };
        case "07":
            return { value: "07", text: NHS_NUMBER_STATUS_TEXT["07"] };
    }
}
/**
 * eMHA's status in `traceIsNeeded` terms, narrowed so a caller that decides to
 * trace has the link holding the number to trace with.
 */
export function needsPdsTrace(status) {
    return traceIsNeeded(status.value);
}
//# sourceMappingURL=nhsNumberIndicatorStatus.js.map